<template>
  <div class="achats">

    <main class="achats__main">

      <transition
        leave-active-class="d-none"
      >
        <div
          v-if="fetchState.status.value === 'pending'"
          class="achats__loading"
        >
          <ui-spinner
            :type="UI.SPINNER_TYPE.BORDER"
            :size="UI.SIZE.MEDIUM"
            class="color-gray-90 mx-auto my-3"
          />
        </div>
      </transition>

      <transition
        enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
        leave-active-class="animate__animated animate__fadeOut ease-in-out a3"
      >
        <div
          v-if="fetchState.status.value === 'success'"
          class="achats__grid"
        >

          <!-- Maintenance section -->
          <ui-cloak
            :is-visible="fetchState.status.value === 'success'"
            :idle-phase-duration="500"
          >
            <template #cloak>
              <loading-section />
            </template>
            <template #default>
              <transition
                enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
                leave-active-class="animate__animated animate__fadeOut ease-in-out a3"
              >
                <section-reco
                  v-if="!contractData"
                  name="Maintenance"
                  icon-name="build"
                >
                  <template #description>
                    Notre service de maintenance vous assistera lors de problèmes
                  </template>
                </section-reco>
                <maintenance-section
                  v-else
                  :contract-data="contractData"
                />
              </transition>
            </template>
          </ui-cloak>

          <!-- Signature section -->
          <ui-cloak
            :is-visible="fetchState.status.value === 'success'"
            :idle-phase-duration="500"
          >
            <template #cloak>
              <loading-section />
            </template>
            <template #default>
              <transition
                enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
                leave-active-class="animate__animated animate__fadeOut ease-in-out a3"
              >
                <section-reco
                  v-if="!signatureData"
                  name="Signature électronique"
                  icon-name="edit"
                >
                  <template #description>
                    Vous souhaitez faire signer électroniquement vos contrats de travail et déposer ces derniers sur l'espace collaborateur Transat ?
                  </template>
                </section-reco>
                <signature-section
                  v-else
                  :current-signatures="signatureData.total_signatures_restantes ?? undefined"
                  :max-signatures="signatureData.total_signatures ?? undefined"
                />
              </transition>
            </template>
          </ui-cloak>

          <!-- Rights section -->
          <ui-cloak
            :is-visible="fetchState.status.value === 'success'"
            :idle-phase-duration="500"
          >
            <template #cloak>
              <loading-section />
            </template>
            <template #default>
              <transition-group
                enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
                leave-active-class="animate__animated animate__fadeOut ease-in-out a3"
              >
                <section-reco
                  v-if="!daData || !daData.actif"
                  key="rights-section-reco"
                  name="Droits d'auteur"
                  icon-name="copyright"
                  button-text="Contactez notre équipe"
                >
                  <template #description>
                    Vous rémunérez des auteurs, vous devez déclarer des honoraires ?
                    <br>
                    Découvrez notre module spécifique :<br>
                    déclaration Agessa-Urssaf, Ircec, DAS2… tout y est, en un clic&nbsp;!
                  </template>
                </section-reco>

                <rights-section
                  v-if="daData && daData.actif"
                  key="rights-section"
                  :packs="daData.packs"
                />
              </transition-group>
            </template>
          </ui-cloak>

          <!-- Api section -->
          <ui-cloak
            :is-visible="fetchState.status.value === 'success'"
            :idle-phase-duration="500"
          >
            <template #cloak>
              <loading-section />
            </template>
            <template #default>
              <transition
                enter-active-class="animate__animated animate__fadeIn ease-in-out a3"
                leave-active-class="animate__animated animate__fadeOut ease-in-out a3"
              >
                <section-reco
                  v-if="!hasApiAccess"
                  name="API"
                  icon-name="computer"
                  button-text="Contactez notre équipe"
                >
                  <template #description>
                    Vous souhaitez connecter une solution métier de manière fluide et automatisée à votre logiciel de paie ?
                    <br>
                    Découvrez les possibilités offertes par les connecteurs APIs et les connexions déjà disponibles.
                  </template>
                </section-reco>
                <api-section v-else />
              </transition>
            </template>
          </ui-cloak>

        </div>
      </transition>

    </main>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/sass/modules/layout/members'
@import '~/assets/sass/modules/dashboard/variables'

.achats
  min-width: map-get($breakpoints, 'tablet')

  &__main
    width: 100%
    padding: 20px

  &__loading
    display: flex
    justify-content: center
    padding: space-x(4)

  &__grid
    display: grid
    position: relative
    grid-template-columns: repeat(2, 1fr)
    grid-template-rows: repeat(2, 1fr)
    column-gap: space-x(4)
    row-gap: space-x(2)
    background: linear-gradient(to bottom, $background-secondary, $background-tertiary)
    padding: space-x(2)
    box-shadow: 3px 3px 0 0 $border-color
    border-radius: $border-radius-lg
    min-height: $section-min-height

    @include media('<1260px')
      grid-template-columns: 1fr

</style>

<script lang="ts">
import MaintenanceSection from '~/components/dashboard/MaintenanceSection.vue'
import SignatureSection from '~/components/dashboard/SignatureSection.vue'
import RightsSection from '~/components/dashboard/RightsSection.vue'
import ApiSection from '~/components/dashboard/ApiSection.vue'
import LoadingSection from '~/components/dashboard/LoadingSection.vue'
import SectionReco from '~/components/dashboard/SectionReco.vue'
import { any, filter, isNil, pathOr, pipe, none, toString, each } from '~/utils/lib/core/prelude'
import { MENU_ITEM, NAVIGATION_SCOPE } from '~/utils/lib/layout/types'
import { useStore_parametres_achats, type DaInfos, type InfosContrat, type InfosSignature } from '~/stores/parametres/achats'
import { useLegacyPageFetch } from '~/composables/core/hook'
import { useToastBuilder } from '~/composables/layout/toast'
import { useDataOnlyNavigationContext } from '~/composables/layout/navigation'
import { useAccessControl } from '~/composables/layout/access-control'
import Console from '~/utils/lib/core/console'

const console = new Console('component:page-parametres-achats')

export default defineNuxtComponent({
  components: {
    MaintenanceSection,
    SignatureSection,
    RightsSection,
    ApiSection,
    LoadingSection,
    SectionReco,
  },
  setup() {
    definePageMeta({
      navigationScope: NAVIGATION_SCOPE.DATA_ONLY,
      menuItem: MENU_ITEM.PARAMETERS_ACHATS,
      middleware: [
        'session-control-gate',
      ]
    })

    const navigationContext = useDataOnlyNavigationContext()
    useHead({
      title: 'Achats',
    })

    const {
      fetchState,
      fetchWrapper,
    } = useLegacyPageFetch()

    const ToastBuilder = useToastBuilder()

    const store_parametres_achats = useStore_parametres_achats()

    return {
      ...navigationContext,
      ...useAccessControl(),
      fetchState,
      fetchWrapper,
      ToastBuilder,
      store_parametres_achats,
    }
  },
  data() {
    return {
      signatureData: null as null|InfosSignature,
      contractData: null as null|InfosContrat,
      daData: null as null|DaInfos,
      hasApiAccess: false,
    }
  },
  created() {
    this.fetchWrapper(
      () => this.fetch()
    )
  },
  methods: {
    fetch() {
      const {
        structure: { code_client, uuidData, societes },
      } = this.fromStructureLevel()

      const firstCompany = Object.values(societes)[0]
      const code_societe = firstCompany?.code_societe

      return Promise.allSettled([
        this.store_parametres_achats.infosSignature({ code_client }),
        this.store_parametres_achats.daInfos({ code_client }),
        this.store_parametres_achats.contratInfos({ code_client }),
        ...(code_societe
          ? [this.store_parametres_achats.infosApi({ uuidData, code_societe })]
          : []
        ),
      ]).then((results) => {
        const hasError = any(
          (result) => result.status === 'rejected',
          results
        )

        if (results[0].status !== 'rejected')
          this.signatureData = results[0].value

        if (results[1].status !== 'rejected')
          this.daData = results[1].value

        if (results[2].status !== 'rejected')
          this.contractData = results[2].value

        if (results[3] && results[3].status !== 'rejected')
          this.hasApiAccess = results[3].value.has_access_to_api

        if (hasError) {
          this.handleFetchErrors(results)
        }
      })
    },
    handleFetchErrors(errors: PromiseSettledResult<any>[]) {
      const rejectedResults = filter((result) => result.status === 'rejected', errors)

      const hasTrueError = (result: any) => {
        const accessGranted = pathOr(null, ['reason', 'payload', 'err', 'accessGranted'], result)
        
        if (!isNil(accessGranted)) {
          return false
        }
        
        const httpStatus = pathOr(null, ['reason', 'httpStatus'], result)
        if (httpStatus === 404) {
          return false
        }
        
        return true
      }

      if (none(hasTrueError, rejectedResults))
        return

      const errorResponseList = filter(hasTrueError, rejectedResults)

      console.warn(
        'handleFetchErrors',
        errorResponseList
      )

      each.iterable(
        response => this.ToastBuilder
          .error(toString(response.reason))
          .title('Erreur lors de la récupération des données')
          .show(),
        errorResponseList
      )
    },
  }
})
</script>
